import { createContext, type PropsWithChildren, type ReactNode, useContext, useEffect, useState } from 'react'
import { type KeycloakInitOptions, type KeycloakTokenParsed } from 'keycloak-js'
import { LoadingIndicator } from '@dhl/components'
import { useKeycloakInstance } from './KeycloakInstanceProvider'

export type ContextType = {
  token?: string
  tokenParsed?: KeycloakTokenParsed
  sessionId?: string
  refreshTokenParsed?: KeycloakTokenParsed
  isFetchingNewToken: boolean | Promise<boolean | string>
}

const Context = createContext<ContextType>({
  isFetchingNewToken: false,
})

type Props = PropsWithChildren<{
  /**
   * When no options are given we set the requirement the user allways has to login first.
   * @default: { onLoad: 'login-required' }
   */
  initOptions?: KeycloakInitOptions

  /**
   * Option to pass your own loading view for nicely startup of your application
   */
  Loading?: ReactNode
}>

/**
 * Our default is that we allways need to have the user to be logged in :)
 */
const defaultInitOptions: KeycloakInitOptions = {
  onLoad: 'login-required',
}

export const KeycloakTokensProvider = ({
  initOptions = defaultInitOptions,
  // reason: uppercase so its clear its an react node
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Loading = <LoadingIndicator />,
  children,
}: Props) => {
  const instance = useKeycloakInstance()

  const [authContext, setAuthContext] = useState<ContextType>({
    token: instance.token,
    tokenParsed: instance.tokenParsed,
    sessionId: instance.sessionId,
    refreshTokenParsed: instance.refreshTokenParsed,
    isFetchingNewToken: !!instance.token,
  })

  useEffect(() => {
    const onRefresh = () => {
      setAuthContext((prev) => ({
        ...prev,
        isFetchingNewToken: instance
          .updateToken(5)
          .catch(() => {
            // When fetching a new token fails and the refresh token has expired,
            // than we can no longer do anything useful, therefor redirect to login
            if (
              initOptions.onLoad === 'login-required' &&
              instance.refreshTokenParsed?.exp &&
              new Date().getTime() / 1000 > instance.refreshTokenParsed?.exp
            ) {
              instance.login()
            }
            return false
          })
          .then(() => instance.token || '')
          .finally(() => {
            setAuthContext((prevs) => ({
              ...prevs,
              isFetchingNewToken: false,
            }))
          }),
      }))
    }

    const onUpdate = () => {
      setAuthContext((prev) => ({
        ...prev,
        token: instance.token,
        tokenParsed: instance.tokenParsed,
        sessionId: instance.sessionId,
        refreshTokenParsed: instance.refreshTokenParsed,
      }))
    }

    instance.onReady = onUpdate
    instance.onAuthSuccess = onUpdate
    instance.onAuthRefreshSuccess = onUpdate
    instance.onTokenExpired = onRefresh

    if (!instance.didInitialize) {
      instance.init(initOptions)
    }

    return () => {
      instance.onReady = undefined
      instance.onAuthRefreshSuccess = undefined
      instance.onAuthSuccess = undefined
      instance.onTokenExpired = undefined
    }
  }, [instance, initOptions])

  if (!instance.didInitialize) {
    return Loading
  }

  return <Context.Provider value={authContext}>{children}</Context.Provider>
}

export const useKeycloakTokens = () => useContext(Context)
